import { Navigate, useParams } from "react-router-dom";
import { useAccess } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { EmptyState } from "../../components/ui";
import { ACCESS_SECTIONS } from "../../lib/modules";
import { ModulePage } from "../modules/ModulePage";
import { UsersPage } from "../UsersPage";
import { EnvironmentsSection } from "./EnvironmentsSection";
import { GroupsSection } from "./GroupsSection";
import { PeopleSection } from "./PeopleSection";
import { RolesSection } from "./RolesSection";

const TITLES: Record<string, string> = { people: "Access · People", roles: "Access · Roles", groups: "Access · Groups", environments: "Access · Environments" };

/**
 * The Access module (ADR-0017 section 6): who holds what on this
 * environment (People), what a role lets its holder do (Roles), sets of
 * roles for several people at once (Groups), and the environment's chain
 * and switches (Environments). Each section is behind its own `enabled`
 * switch; the API refuses what the screen hides.
 */
export function AccessPage() {
  const { me } = useAuth();
  const { section = "people" } = useParams();
  const access = useAccess();
  const title = TITLES[section];
  if (!title) return <Navigate to=".." replace />;
  const needs = ACCESS_SECTIONS.find((s) => s.id === section)?.needs ?? [];
  return (
    <ModulePage module="access" section={section === "people" ? undefined : section} title={title}>
      {(_d, environmentId) => {
        if (!access.isPending && !access.set(environmentId).hasAny(needs)) {
          return <EmptyState title="This section is not available to you here" hint="No role you hold on this environment enables it." />;
        }
        switch (section) {
          case "roles":
            return <RolesSection environmentId={environmentId} />;
          case "groups":
            return <GroupsSection environmentId={environmentId} />;
          case "environments":
            return <EnvironmentsSection environmentId={environmentId} />;
          default:
            return (
              <div className="grid gap-8">
                <PeopleSection environmentId={environmentId} />
                {(me?.role === "customer" || me?.viewAs) && <UsersPage embedded />}
              </div>
            );
        }
      }}
    </ModulePage>
  );
}
